/**
 * Maintenance Tickets hooks using React Relay
 *
 * These hooks provide access to maintenance ticket data through Relay's
 * normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLazyLoadQuery, usePaginationFragment, fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useMaintenanceTicketsRelayQuery as UseMaintenanceTicketsRelayQuery,
  useMaintenanceTicketsRelayQuery$variables,
  MaintenanceTicketFilterInput,
  MaintenanceTicketSortInput,
} from './__generated__/useMaintenanceTicketsRelayQuery.graphql';
import type { useMaintenanceTicketsRelayDirectQuery as UseMaintenanceTicketsRelayDirectQuery } from './__generated__/useMaintenanceTicketsRelayDirectQuery.graphql';
import type { useMaintenanceTicketsRelayByIdQuery } from './__generated__/useMaintenanceTicketsRelayByIdQuery.graphql';
import type { useMaintenanceTicketsRelayPaginationFragment$key } from './__generated__/useMaintenanceTicketsRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Pagination fragment for maintenance tickets list
 */
const MaintenanceTicketsListPaginationFragment = graphql`
  fragment useMaintenanceTicketsRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useMaintenanceTicketsRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "MaintenanceTicketFilterInput" }
    sort: { type: "[MaintenanceTicketSortInput!]" }
    filterTags: { type: "[String!]" }
    search: { type: "String" }
  ) {
    listMaintenanceTickets(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
      filterTags: $filterTags
      search: $search
    )
      @connection(
        key: "useMaintenanceTicketsRelayPaginationFragment_listMaintenanceTickets"
        filters: ["filter", "sort", "filterTags", "search"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          assignee
          canceledOn
          communicationLog
          completedOn
          createdOn
          description
          dueOn
          errorReportOldId
          errorReport {
            id
          }
          hardwareOldId
          maintenanceTaskId
          organizationId
          priority
          rentalOldId
          reporter
          sourceType
          status
          systemId
          systemOldId
          tags
          images
          assigneeUser {
            id
            email
            firstName
            lastName
          }
          reporterUser {
            id
            email
            firstName
            lastName
          }
          hardware {
            id
            oldId
            number
            mac
            imei
            vendorSerial
            label
            hardwareType {
              id
              name
            }
            hardwareProduct {
              id
              primaryIdentifier
            }
          }
          maintenanceTask {
            id
            oldId
            name
            description
            taskType
          }
          rental {
            id
            oldId
          }
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

/**
 * Query for paginated maintenance tickets list (uses fragment for Suspense hooks)
 */
const MaintenanceTicketsListQuery = graphql`
  query useMaintenanceTicketsRelayQuery(
    $first: Int
    $after: String
    $filter: MaintenanceTicketFilterInput
    $sort: [MaintenanceTicketSortInput!]
    $filterTags: [String!]
    $search: String
  ) {
    ...useMaintenanceTicketsRelayPaginationFragment
      @arguments(
        first: $first
        after: $after
        filter: $filter
        sort: $sort
        filterTags: $filterTags
        search: $search
      )
  }
`;

/**
 * Direct query for maintenance tickets list (for non-Suspense fetchQuery usage)
 */
const MaintenanceTicketsListDirectQuery = graphql`
  query useMaintenanceTicketsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: MaintenanceTicketFilterInput
    $sort: [MaintenanceTicketSortInput!]
    $filterTags: [String!]
    $search: String
  ) {
    listMaintenanceTickets(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
      filterTags: $filterTags
      search: $search
    ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          assignee
          canceledOn
          communicationLog
          completedOn
          createdOn
          description
          dueOn
          errorReportOldId
          errorReport {
            id
          }
          hardwareOldId
          maintenanceTaskId
          organizationId
          priority
          rentalOldId
          reporter
          sourceType
          status
          systemId
          systemOldId
          tags
          images
          assigneeUser {
            id
            email
            firstName
            lastName
          }
          reporterUser {
            id
            email
            firstName
            lastName
          }
          hardware {
            id
            oldId
            number
            mac
            imei
            vendorSerial
            label
            hardwareType {
              id
              name
            }
            hardwareProduct {
              id
              primaryIdentifier
            }
          }
          maintenanceTask {
            id
            oldId
            name
            description
            taskType
          }
          rental {
            id
            oldId
          }
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

/**
 * Query for single maintenance ticket by ID
 */
const MaintenanceTicketByIdQuery = graphql`
  query useMaintenanceTicketsRelayByIdQuery($id: ID!) {
    getMaintenanceTicket(id: $id) {
      id
      oldId
      assignee
      canceledOn
      communicationLog
      completedOn
      createdOn
      description
      dueOn
      errorReportOldId
      errorReport {
        id
      }
      hardwareOldId
      maintenanceTaskId
      organizationId
      priority
      rentalOldId
      reporter
      sourceType
      status
      systemId
      systemOldId
      tags
      images
      assigneeUser {
        id
        email
        firstName
        lastName
      }
      reporterUser {
        id
        email
        firstName
        lastName
      }
      hardware {
        id
        oldId
        number
        mac
        imei
        vendorSerial
        label
        hardwareType {
          id
          name
        }
        hardwareProduct {
          id
          primaryIdentifier
        }
        location {
          id
          name
        }
      }
      maintenanceTask {
        id
        oldId
        name
        description
        taskType
      }
      rental {
        id
        oldId
      }
      system {
        id
        oldId
        name
      }
      usageProgress {
        current
        threshold
        ticketCreatedOn
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { MaintenanceTicketFilterInput, MaintenanceTicketSortInput };

export interface AdminUserGraphQL {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface HardwareTypeGraphQL {
  id: string;
  name: string;
}

export interface HardwareProductGraphQL {
  id: string;
  primaryIdentifier?: string | null;
}

export interface TicketHardwareLocationGraphQL {
  id: string;
  name?: string | null;
}

export interface TicketHardwareGraphQL {
  id: string;
  oldId?: string | null;
  number?: number | null;
  mac?: string | null;
  imei?: string | null;
  vendorSerial?: string | null;
  label?: string | null;
  hardwareType?: HardwareTypeGraphQL | null;
  hardwareProduct?: HardwareProductGraphQL | null;
  location?: TicketHardwareLocationGraphQL | null;
}

export interface TicketMaintenanceTaskGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  description: string;
  taskType?: string | null;
}

export interface TicketRentalGraphQL {
  id: string;
  oldId?: string | null;
}

export interface TicketSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

export interface MaintenanceTicketGraphQL {
  id: string;
  oldId?: string | null;
  assignee?: string | null;
  canceledOn?: string | null;
  communicationLog?: readonly unknown[] | null;
  completedOn?: string | null;
  createdOn: string;
  description: string;
  dueOn: string;
  errorReportOldId?: string | null;
  errorReport?: { id: string } | null;
  hardwareOldId?: string | null;
  maintenanceTaskId: string;
  organizationId?: string | null;
  priority: number;
  rentalOldId?: string | null;
  reporter?: string | null;
  sourceType: string;
  status?: string | null;
  systemId?: string | null;
  systemOldId: string;
  tags?: readonly string[] | null;
  images?: readonly unknown[] | null;
  assigneeUser?: AdminUserGraphQL | null;
  reporterUser?: AdminUserGraphQL | null;
  hardware?: TicketHardwareGraphQL | null;
  maintenanceTask?: TicketMaintenanceTaskGraphQL | null;
  rental?: TicketRentalGraphQL | null;
  system?: TicketSystemGraphQL | null;
  usageProgress?: {
    current: number;
    threshold: number;
    ticketCreatedOn?: string | null;
  } | null;
}

export type MaintenanceTicketStatus = 'open' | 'closed' | 'canceled';

export interface MaintenanceTicketQueryFilter {
  ticketId?: string;
  systemId?: string;
  systemOldId?: string;
  organizationId?: string;
  hardwareOldId?: string;
  /**
   * Status filter where 'open' means "not completed and not canceled"
   * regardless of assignee. Multiple values are OR'd together; 'all' or an
   * empty array applies no status filtering.
   */
  status?: MaintenanceTicketStatus | 'all' | MaintenanceTicketStatus[];
  /**
   * Multi-select status filter that also distinguishes "open" (unassigned) from
   * "assigned". Conditions are combined with OR. Use this instead of `status`
   * when the caller needs the Open/Assigned distinction (e.g. hardware detail).
   */
  statuses?: Array<'open' | 'assigned' | 'closed' | 'canceled'>;
  tags?: string[];
  /**
   * Case-insensitive substring search across ticket text fields (description,
   * assignee, reporter) and the unit's identifiers (number, label, MAC, IMEI,
   * vendor ID, vendor serial, vendor QR code).
   */
  search?: string;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching maintenance tickets list using Relay (Suspense)
 */
export function useMaintenanceTicketsRelay(
  filter?: MaintenanceTicketQueryFilter,
  options?: {
    limit?: number;
    sort?: MaintenanceTicketSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];

  const { relayFilter, filterTags } = buildRelayFilterWithArgs(filter);

  const variables: useMaintenanceTicketsRelayQuery$variables = {
    first: limit,
    filter: relayFilter,
    sort,
    filterTags,
  };

  const queryData = useLazyLoadQuery<UseMaintenanceTicketsRelayQuery>(
    MaintenanceTicketsListQuery,
    variables
  );

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseMaintenanceTicketsRelayQuery,
    useMaintenanceTicketsRelayPaginationFragment$key
  >(MaintenanceTicketsListPaginationFragment, queryData);

  const nodes =
    data.listMaintenanceTickets?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as MaintenanceTicketGraphQL[],
    count: data.listMaintenanceTickets?.count,
    pageInfo: data.listMaintenanceTickets?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Hook for fetching a single maintenance ticket by ID using Relay (Suspense)
 */
export function useMaintenanceTicketByIdRelay(ticketId: string) {
  const data = useLazyLoadQuery<useMaintenanceTicketsRelayByIdQuery>(
    MaintenanceTicketByIdQuery,
    { id: ticketId }
  );

  return data.getMaintenanceTicket as MaintenanceTicketGraphQL | null;
}

/**
 * Non-Suspense hook for fetching a single maintenance ticket by ID.
 * Provides React Query-like interface with isLoading, data, refetch.
 */
export function useMaintenanceTicketByIdRelayQuery(
  ticketId: string,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<MaintenanceTicketGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && !!ticketId;

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          // Don't reset loading on refetch
        } else {
          setIsLoading(true);
        }

        const result = await fetchQuery<useMaintenanceTicketsRelayByIdQuery>(
          relayEnvironment,
          MaintenanceTicketByIdQuery,
          { id: ticketId }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData((result.getMaintenanceTicket as MaintenanceTicketGraphQL) ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch maintenance ticket')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ticketId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    isLoading,
    isFetched,
    error,
    refetch,
  };
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for maintenance tickets data.
 * Does NOT use Suspense - provides isLoading, data, refetch etc.
 */
export function useMaintenanceTicketsRelayQuery(
  filter?: MaintenanceTicketQueryFilter,
  options?: {
    limit?: number;
    sort?: MaintenanceTicketSortInput[];
    enabled?: boolean;
    after?: string | null;
  }
) {
  const [data, setData] = useState<MaintenanceTicketGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED_ON' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;
  const after = options?.after || undefined;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const { relayFilter, filterTags } = buildRelayFilterWithArgs(filter);
        const variables: Record<string, unknown> = {
          first: limit,
          sort,
        };
        if (after) {
          variables.after = after;
        }
        if (relayFilter) {
          variables.filter = relayFilter;
        }
        if (filterTags && filterTags.length > 0) {
          variables.filterTags = filterTags;
        }

        const result = await fetchQuery<UseMaintenanceTicketsRelayDirectQuery>(
          relayEnvironment,
          MaintenanceTicketsListDirectQuery,
          variables as useMaintenanceTicketsRelayQuery$variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listMaintenanceTickets?.edges
              ?.map((edge) => edge?.node)
              .filter(Boolean) ?? [];
          setData(nodes as MaintenanceTicketGraphQL[]);
          setCount(result.listMaintenanceTickets?.count ?? undefined);
          setHasMore(result.listMaintenanceTickets?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listMaintenanceTickets?.pageInfo?.endCursor ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch maintenance tickets')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, after, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

const VALID_STATUS_PARAMS: readonly MaintenanceTicketStatus[] = [
  'open',
  'closed',
  'canceled',
];

/**
 * Parses the comma-separated status URL-state param used by the maintenance
 * tickets pages into the hook's `status` filter. An empty selection means the
 * user removed the filter, so no status filtering is applied at all.
 */
export function parseStatusFilterParam(status?: string): MaintenanceTicketStatus[] {
  if (!status) return [];
  return status
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s): s is MaintenanceTicketStatus =>
      (VALID_STATUS_PARAMS as readonly string[]).includes(s)
    );
}

/**
 * Builds Relay filter + top-level args from simplified filter interface
 */
function buildRelayFilterWithArgs(filter?: MaintenanceTicketQueryFilter): {
  relayFilter: MaintenanceTicketFilterInput | undefined;
  filterTags: string[] | undefined;
} {
  if (!filter) return { relayFilter: undefined, filterTags: undefined };

  const relayFilter: MaintenanceTicketFilterInput = {};
  let filterTags: string[] | undefined;

  if (filter.ticketId) {
    relayFilter.id = { eq: filter.ticketId };
  }
  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.hardwareOldId) {
    relayFilter.hardwareOldId = { eq: filter.hardwareOldId };
  }

  // Status filter mapping. 'open' means "not completed and not canceled"
  // regardless of assignee. 'all' / empty means no status filter. Multiple
  // statuses are OR'd together and AND'd with the other top-level conditions.
  //
  // We filter on the stored `status` computed column (which holds exactly
  // 'open' | 'closed' | 'canceled') instead of `canceledOn`/`completedOn`
  // isNil predicates. The isNil form can't use any index, so CockroachDB had
  // to scan every ticket in the system/org (~15s, hitting the Ecto timeout);
  // `status` is backed by maintenance_ticket_filter_idx (system_id, status,
  // created_on DESC), which serves the whole query in a few hundred ms.
  const statusList: MaintenanceTicketStatus[] = Array.isArray(filter.status)
    ? filter.status
    : filter.status && filter.status !== 'all'
    ? [filter.status]
    : [];

  const statusCondition = (s: MaintenanceTicketStatus): MaintenanceTicketFilterInput => ({
    status: { eq: s },
  });

  if (statusList.length === 1) {
    Object.assign(relayFilter, statusCondition(statusList[0]));
  } else if (statusList.length > 1) {
    relayFilter.or = statusList.map(statusCondition);
  }

  // Multi-select status mapping (distinguishes Open vs Assigned), OR'd together.
  // AND'd with any other top-level conditions (e.g. hardwareOldId).
  if (filter.statuses && filter.statuses.length > 0) {
    const statusConditions = filter.statuses
      .map((s): MaintenanceTicketFilterInput | null => {
        switch (s) {
          case 'canceled':
            return { status: { eq: 'canceled' } };
          case 'closed':
            return { status: { eq: 'closed' } };
          case 'assigned':
            // "open" ticket (per the stored status column) that has an
            // assignee. The status eq uses maintenance_ticket_filter_idx;
            // the assignee predicate then narrows the small open set.
            return {
              and: [{ status: { eq: 'open' } }, { assignee: { isNil: false } }],
            };
          case 'open':
            // "open" ticket with no assignee. See above re: index usage.
            return {
              and: [{ status: { eq: 'open' } }, { assignee: { isNil: true } }],
            };
          default:
            return null;
        }
      })
      .filter((c): c is MaintenanceTicketFilterInput => c !== null);

    if (statusConditions.length > 0) {
      relayFilter.or = statusConditions;
    }
  }

  // Tags go to top-level filterTags arg
  if (filter.tags && filter.tags.length > 0) {
    filterTags = [...filter.tags];
  }

  // Search is a filter-level OR instead of the backend `search` argument so
  // it also matches unit identifiers the backend search misses (number,
  // vendor ID, vendor QR code). Ticket fields mirror the backend search
  // (description, assignee, reporter). The API rejects `or` nested inside
  // the hardware relationship filter, so each identifier repeats the
  // relationship at the top level.
  if (filter.search) {
    const pattern = `%${filter.search}%`;
    const searchOr: MaintenanceTicketFilterInput[] = [
      { description: { ilike: pattern } },
      { assignee: { ilike: pattern } },
      { reporter: { ilike: pattern } },
      { hardware: { numberText: { ilike: pattern } } },
      { hardware: { label: { ilike: pattern } } },
      { hardware: { mac: { ilike: pattern } } },
      { hardware: { imei: { ilike: pattern } } },
      { hardware: { vendorId: { ilike: pattern } } },
      { hardware: { vendorSerial: { ilike: pattern } } },
      { hardware: { vendorQrCode: { ilike: pattern } } },
    ];
    if (relayFilter.or) {
      // the status filter already claimed the top-level or; AND the groups
      relayFilter.and = [
        ...(relayFilter.and ?? []),
        { or: relayFilter.or },
        { or: searchOr },
      ];
      delete relayFilter.or;
    } else {
      relayFilter.or = searchOr;
    }
  }

  const hasFilter = Object.keys(relayFilter).length > 0;

  return {
    relayFilter: hasFilter ? relayFilter : undefined,
    filterTags,
  };
}

/**
 * Simple filter builder (without top-level args) for use with just the filter input
 */
export function buildRelayFilter(
  filter?: MaintenanceTicketQueryFilter
): MaintenanceTicketFilterInput | undefined {
  return buildRelayFilterWithArgs(filter).relayFilter;
}

export { buildRelayFilterWithArgs };
